import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiCalendar,
  FiSave,
  FiAlertCircle,
  FiKey,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";

import { User, Appointment, WorkoutPlanFeedback } from "../../../types";
import students from "../../../services/students";
import appointment from "../../../services/appointment";
import utils from "../../../helpers/utils";
import { AppointmentItem } from "../../appointments/AppointmentItem";
import workoutplans from "../../../services/workoutplans";

interface StudentProfileTabProps {
  student: User;
  professionalId: string;
  onClose: () => void;
}

type ProfileForm = {
  name: string;
  email: string;
  phone: string;
  birth_date: string;
  gener: string;
  user_validity: string;
};

const formatDate = (date?: string) => (date ? date.slice(0, 10) : "");

const translateStatus = (status: string): string => {
  switch (status) {
    case "confirmed":
      return "Confirmado";
    case "cancelled":
      return "Cancelado";
    case "scheduled":
      return "Agendado";
    default:
      return status;
  }
};

const StudentProfileTab: React.FC<StudentProfileTabProps> = ({
  student,
  professionalId,
  onClose,
}) => {
  const [savingProfile, setSavingProfile] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [isPersonalDataExpanded, setIsPersonalDataExpanded] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileForm>({
    name: "",
    email: "",
    phone: "",
    birth_date: "",
    gener: "",
    user_validity: "",
  });
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [feedbacks, setFeedbacks] = useState<WorkoutPlanFeedback[]>([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState<
    "appointments" | "feedbacks"
  >("appointments");
  const [hasChanges, setHasChanges] = useState(false);
  const [originalForm, setOriginalForm] = useState<ProfileForm>({
    name: "",
    email: "",
    phone: "",
    birth_date: "",
    gener: "",
    user_validity: "",
  });

  // Detectar se é mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsPersonalDataExpanded(window.innerWidth >= 1024); // lg breakpoint
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    if (!student) return;

    const initialForm = {
      name: student.name ?? "",
      email: student.email ?? "",
      phone: student.phone ?? "",
      birth_date: formatDate(student.birth_date),
      gener: student.gener ?? "",
      user_validity: formatDate(student.user_validity),
    };

    setProfileForm(initialForm);
    setOriginalForm(initialForm);
    setHasChanges(false);
  }, [student]);

  // Verificar se houve mudanças sempre que o formulário mudar
  useEffect(() => {
    if (!student) return;

    const hasFormChanged =
      profileForm.name !== originalForm.name ||
      profileForm.email !== originalForm.email ||
      profileForm.phone !== originalForm.phone ||
      profileForm.birth_date !== originalForm.birth_date ||
      profileForm.gener !== originalForm.gener ||
      profileForm.user_validity !== originalForm.user_validity;

    setHasChanges(hasFormChanged);
  }, [profileForm, originalForm, student]);

  useEffect(() => {
    const loadAppointments = async () => {
      if (!student?.id) return;
      setLoadingAppointments(true);
      try {
        const data = await appointment.getStudentAppointments(
          String(student.id),
        );
        setAppointments(data);
      } catch (error) {
        console.error("Erro ao carregar agendamentos:", error);
      } finally {
        setLoadingAppointments(false);
      }
    };

    loadAppointments();
  }, [student?.id]);

  useEffect(() => {
    const loadFeedbacks = async () => {
      if (!student?.id) return;
      setLoadingFeedbacks(true);
      try {
        const data = await workoutplans.listFeedbacksByUser(String(student.id));
        setFeedbacks(data || []);
      } catch (error) {
        console.error("Erro ao carregar feedbacks de treinos:", error);
      } finally {
        setLoadingFeedbacks(false);
      }
    };

    loadFeedbacks();
  }, [student?.id]);

  const handleChange = useCallback(
    (field: keyof ProfileForm) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        if (field === "phone") {
          const digitsOnly = e.target.value.replace(/\D/g, "");
          setProfileForm((prev) => ({
            ...prev,
            [field]: digitsOnly,
          }));
          return;
        }
        setProfileForm((prev) => ({
          ...prev,
          [field]: e.target.value,
        }));
      },
    [],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      const payload: Partial<User> = {
        name: profileForm.name,
        email: profileForm.email,
        phone: profileForm.phone.replace(/\D/g, ""), // Remove caracteres não numéricos
        birth_date: profileForm.birth_date || undefined,
        gener: profileForm.gener || undefined,
        user_validity: profileForm.user_validity || undefined,
      };

      const updated = await students.updateProfile(String(student.id), payload);

      if (!updated) {
        throw new Error("Falha ao atualizar perfil");
      }

      if (profileForm.user_validity) {
        await students.renew({
          professional_id: professionalId,
          user_id: String(student.id),
          new_validity: profileForm.user_validity,
        });
      }

      toast.success("Perfil atualizado com sucesso!");

      const updatedForm = {
        name: updated.name ?? "",
        email: updated.email ?? "",
        phone: updated.phone ?? "",
        birth_date: formatDate(updated.birth_date),
        gener: updated.gener ?? "",
        user_validity: formatDate(updated.user_validity),
      };

      setProfileForm(updatedForm);
      setOriginalForm(updatedForm);
      setHasChanges(false);
    } catch (error) {
      toast.error("Erro ao atualizar perfil.");
      console.error(error);
    } finally {
      setSavingProfile(false);
    }
  };

  const isValidityExpiring = () => {
    if (!profileForm.user_validity) return false;
    const validityDate = new Date(profileForm.user_validity);
    const today = new Date();
    const diffDays = Math.ceil(
      (validityDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    return diffDays > 0 && diffDays <= 7;
  };

  const isValidityExpired = () => {
    if (!profileForm.user_validity) return false;
    return new Date(profileForm.user_validity) < new Date();
  };

  const handleResetPassword = async () => {
    setResettingPassword(true);
    setPasswordResetSent(false);

    try {
      const success = await students.resetPassword(String(student.id));

      if (success) {
        setPasswordResetSent(true);
        toast.success("Nova senha gerada e enviada por email com sucesso!");
      } else {
        throw new Error("Falha ao gerar nova senha");
      }
    } catch (error) {
      toast.error("Erro ao gerar nova senha. Tente novamente.");
      console.error(error);
    } finally {
      setResettingPassword(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!student?.id) return;

    setDownloadingReport(true);
    try {
      const reportData = await appointment.getStudentAppointmentsReport(
        String(student.id),
      );

      if (!reportData) {
        toast.error("Erro ao gerar relatório.");
        return;
      }

      // Preparar dados para Excel
      const workbook = XLSX.utils.book_new();

      // Dados do aluno
      const studentData = [
        ["Relatório de Agendamentos"],
        [""],
        ["Dados do Aluno:"],
        ["Nome", reportData.student?.name || ""],
        ["Email", reportData.student?.email || ""],
        ["Telefone", reportData.student?.phone || ""],
        ["Data de Nascimento", reportData.student?.birth_date || ""],
        [""],
      ];

      const studentSheet = XLSX.utils.aoa_to_sheet(studentData);
      XLSX.utils.book_append_sheet(workbook, studentSheet, "Dados do Aluno");

      // Dados dos agendamentos
      const appointmentsHeader = [
        "ID",
        "Data/Hora Início",
        "Data/Hora Fim",
        "Duração (min)",
        "Tipo",
        "Status",
        "Profissional (Nome)",
        "Profissional (Email)",
      ];

      const appointmentsData: (string | number)[][] = [appointmentsHeader];

      if (reportData.appointments && reportData.appointments.length > 0) {
        reportData.appointments.forEach((apt: Appointment) => {
          const startTime = apt.start_time
            ? utils.toSaoPauloTimeZone(apt.start_time).toLocaleString("pt-BR", {
                timeZone: "America/Sao_Paulo",
              })
            : "";
          const endTime = apt.end_time
            ? utils.toSaoPauloTimeZone(apt.end_time).toLocaleString("pt-BR", {
                timeZone: "America/Sao_Paulo",
              })
            : "";
          const professionalName = apt.professional?.name || "";
          const professionalEmail = apt.professional?.email || "";
          const status = translateStatus(apt.status || "");

          appointmentsData.push([
            apt.id || "",
            startTime,
            endTime,
            apt.duration_minutes || "",
            apt.type || "",
            status,
            professionalName,
            professionalEmail,
          ]);
        });
      } else {
        appointmentsData.push(["Nenhum agendamento encontrado"]);
      }

      const appointmentsSheet = XLSX.utils.aoa_to_sheet(appointmentsData);
      XLSX.utils.book_append_sheet(workbook, appointmentsSheet, "Agendamentos");

      // Gerar arquivo Excel
      const fileName = `relatorio_agendamentos_${student.name || student.id}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success("Relatório baixado com sucesso!");
    } catch (error: unknown) {
      console.error("Erro ao baixar relatório:", error);
      toast.error("Erro ao baixar relatório.");
    } finally {
      setDownloadingReport(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex h-full min-h-0 flex-col pb-0 mb-0"
    >
      {/* Conteúdo scrollável - Mobile First */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Header Section */}
        <div className="mb-3 sm:mb-4">
          <h4 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
            Informações do Perfil
          </h4>
          <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400 sm:mt-1 sm:text-sm">
            Atualize os dados cadastrais e a validade do plano do aluno
          </p>
        </div>

        {/* Dados Pessoais e Agendamentos */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-stretch sm:gap-5 lg:gap-6">
          {/* Dados Pessoais */}
          <div className="flex flex-col overflow-hidden rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark sm:rounded-2xl">
            {/* Header do Card - Clicável no mobile */}
            <button
              type="button"
              onClick={() => setIsPersonalDataExpanded(!isPersonalDataExpanded)}
              className="flex min-h-[44px] items-center justify-between gap-2 p-3 transition-colors touch-manipulation hover:bg-gray-50 dark:hover:bg-gray-900/50 sm:p-4 lg:p-6 lg:pointer-events-none lg:hover:bg-transparent"
            >
              <div className="flex items-center gap-2">
                <div className="h-1 w-6 rounded bg-purple-600 sm:w-8"></div>
                <h5 className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 sm:text-sm">
                  Dados Pessoais
                </h5>
              </div>
              <div className="lg:hidden">
                {isPersonalDataExpanded ? (
                  <FiChevronUp className="text-gray-500" size={20} />
                ) : (
                  <FiChevronDown className="text-gray-500" size={20} />
                )}
              </div>
            </button>

            {/* Conteúdo do Card - Colapsável no mobile */}
            <div
              className={`transition-all duration-300 ease-in-out ${
                isPersonalDataExpanded
                  ? "max-h-[2000px] opacity-100"
                  : "max-h-0 opacity-0 lg:max-h-[2000px] lg:opacity-100"
              }`}
            >
              <div className="flex-1 space-y-4 overflow-x-hidden max-w-full px-3 pb-3 sm:space-y-5 sm:px-4 sm:pb-4 lg:px-6 lg:pb-6">
                {/* Primeira linha: Nome e Email */}
                <div className="grid grid-cols-1 gap-4 min-w-0 sm:grid-cols-2 sm:gap-5">
                  {/* Nome */}
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      Nome Completo <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FiUser
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={handleChange("name")}
                        required
                        placeholder="Nome do aluno"
                        className="w-full min-h-[48px] min-w-0 rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 pl-10 text-sm outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FiMail
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={handleChange("email")}
                        required
                        placeholder="email@exemplo.com"
                        className="w-full min-h-[48px] min-w-0 rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 pl-10 text-sm outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base"
                      />
                    </div>
                  </div>
                </div>

                {/* Segunda linha: Telefone */}
                <div className="grid grid-cols-1 gap-4 min-w-0 sm:grid-cols-2 sm:gap-5">
                  {/* Telefone */}
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      Telefone <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FiPhone
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <input
                        type="text"
                        value={
                          profileForm.phone
                            ? utils.formatarTelefone(profileForm.phone)
                            : ""
                        }
                        onChange={handleChange("phone")}
                        maxLength={16}
                        required
                        placeholder="(00) 00000-0000"
                        className="w-full min-h-[48px] min-w-0 rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 pl-10 text-sm outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base"
                      />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      Sexo
                    </label>
                    <div className="relative">
                      <FiUser
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <select
                        value={profileForm.gener}
                        onChange={handleChange("gener")}
                        className="w-full min-h-[48px] min-w-0 appearance-none rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 pl-10 pr-10 text-sm outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base"
                      >
                        <option value="">Selecione</option>
                        <option value="M">Masculino</option>
                        <option value="F">Feminino</option>
                        <option value="O">Outro</option>
                        <option value="N">Prefiro não informar</option>
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        <FiChevronDown size={18} />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Terceira linha: Data de Nascimento e Validade do Plano */}
                <div className="grid grid-cols-1 gap-4 min-w-0 sm:grid-cols-2 sm:gap-5">
                  {/* Data de Nascimento */}
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      Data de Nascimento
                    </label>
                    <div className="relative">
                      <FiCalendar
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <input
                        type="date"
                        value={profileForm.birth_date}
                        onChange={handleChange("birth_date")}
                        className="w-full min-h-[48px] min-w-0 rounded-xl border border-stroke bg-gray-50 px-3 py-2.5 pl-10 text-sm outline-none transition focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-blue-500 touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base"
                      />
                    </div>
                  </div>

                  {/* Validade do Plano */}
                  <div className="min-w-0">
                    <label className="mb-2 block text-xs font-semibold text-gray-700 dark:text-gray-300 sm:mb-2.5 sm:text-sm">
                      Validade do Plano <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FiCalendar
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 sm:left-4"
                        size={18}
                      />
                      <input
                        type="date"
                        value={profileForm.user_validity}
                        onChange={handleChange("user_validity")}
                        required
                        min={new Date().toISOString().split("T")[0]}
                        className={`w-full min-h-[48px] min-w-0 rounded-xl border px-3 py-2.5 pl-10 text-sm outline-none transition focus:ring-2 dark:bg-form-input dark:text-white touch-manipulation sm:min-h-0 sm:px-4 sm:py-3.5 sm:pl-12 sm:text-base ${
                          isValidityExpired()
                            ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500/20 dark:border-red-800 dark:bg-red-900/20"
                            : isValidityExpiring()
                              ? "border-yellow-500 bg-yellow-50 focus:border-yellow-500 focus:ring-yellow-500/20 dark:border-yellow-800 dark:bg-yellow-900/20"
                              : "border-stroke bg-gray-50 focus:border-blue-500 focus:bg-white focus:ring-blue-500/20 dark:border-strokedark dark:focus:border-blue-500"
                        }`}
                      />
                    </div>
                    {isValidityExpired() && (
                      <div className="mt-1.5 flex items-start gap-2 rounded-xl bg-red-50 px-2.5 py-1.5 dark:bg-red-900/20 sm:mt-2 sm:rounded-lg sm:px-3 sm:py-2">
                        <FiAlertCircle
                          className="mt-0.5 flex-shrink-0 text-red-600 dark:text-red-400"
                          size={14}
                        />
                        <p className="text-[11px] font-medium text-red-700 dark:text-red-400 break-words sm:text-xs">
                          O plano está expirado. Atualize a data de validade.
                        </p>
                      </div>
                    )}
                    {isValidityExpiring() && !isValidityExpired() && (
                      <div className="mt-1.5 flex items-start gap-2 rounded-xl bg-yellow-50 px-2.5 py-1.5 dark:bg-yellow-900/20 sm:mt-2 sm:rounded-lg sm:px-3 sm:py-2">
                        <FiAlertCircle
                          className="mt-0.5 flex-shrink-0 text-yellow-600 dark:text-yellow-400"
                          size={14}
                        />
                        <p className="text-[11px] font-medium text-yellow-700 dark:text-yellow-400 break-words sm:text-xs">
                          O plano está próximo de expirar. Considere renovar.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="px-3 pb-3 sm:px-4 sm:pb-4 lg:px-6 lg:pb-6">
                <button
                  type="button"
                  onClick={handleResetPassword}
                  disabled={resettingPassword || savingProfile}
                  className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-2.5 font-semibold text-white shadow-sm transition-all touch-manipulation disabled:opacity-50 sm:min-h-0 sm:w-auto sm:max-w-[250px] sm:px-6 sm:py-3 md:hover:scale-105 md:hover:shadow-xl md:disabled:hover:scale-100"
                >
                  {resettingPassword ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Gerando nova senha...
                    </>
                  ) : (
                    <>
                      <FiKey size={20} />
                      Gerar Nova Senha
                    </>
                  )}
                </button>
              </div>

              {passwordResetSent && (
                <div className="mx-3 mb-3 flex items-center gap-3 rounded-xl bg-green-50 px-3 py-2.5 dark:bg-green-900/20 sm:mx-4 sm:mb-4 sm:rounded-lg sm:px-4 sm:py-3 lg:mx-6 lg:mb-6">
                  <FiCheckCircle
                    className="text-green-600 dark:text-green-400 flex-shrink-0"
                    size={20}
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                      Nova senha enviada com sucesso!
                    </p>
                    <p className="mt-1 text-xs text-green-700 dark:text-green-400">
                      A nova senha foi gerada e enviada por email para{" "}
                      <strong>{profileForm.email}</strong>. O aluno receberá a
                      senha em breve.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Agendamentos e Feedbacks - Mobile First */}
          <div className="flex min-h-0 max-h-[70vh] flex-col rounded-xl border border-stroke bg-white p-3 shadow-sm dark:border-strokedark dark:bg-boxdark sm:max-h-[600px] sm:rounded-2xl sm:p-4 md:p-6">
            <div className="mb-4 flex flex-col gap-2 sm:mb-5 sm:gap-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <div className="h-1 w-6 rounded bg-blue-600 sm:w-8"></div>
                  <h5 className="text-xs font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 sm:text-sm">
                    Agendamentos e Feedbacks
                  </h5>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadReport}
                  disabled={downloadingReport || loadingAppointments}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-all touch-manipulation disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:rounded-lg md:hover:bg-blue-700 md:hover:shadow-md"
                  title="Baixar relatório de agendamentos"
                >
                  {downloadingReport ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                      Gerando...
                    </>
                  ) : (
                    <>
                      <FiDownload size={16} />
                      Relatório
                    </>
                  )}
                </button>
              </div>
              <div className="inline-flex min-h-[44px] w-full rounded-xl border border-stroke bg-gray-100 p-1 text-xs font-semibold text-gray-600 dark:border-strokedark dark:bg-gray-900/40 dark:text-gray-300 sm:min-h-0 sm:w-auto">
                <button
                  type="button"
                  onClick={() => setActiveInfoTab("appointments")}
                  className={`flex-1 min-h-[40px] rounded-lg px-3 py-2 transition-all touch-manipulation sm:min-h-0 sm:py-1.5 ${
                    activeInfoTab === "appointments"
                      ? "bg-white text-blue-600 shadow-sm dark:bg-boxdark dark:text-blue-400"
                      : "hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  Agendamentos
                </button>
                <button
                  type="button"
                  onClick={() => setActiveInfoTab("feedbacks")}
                  className={`flex-1 min-h-[40px] rounded-lg px-3 py-2 transition-all touch-manipulation sm:min-h-0 sm:py-1.5 ${
                    activeInfoTab === "feedbacks"
                      ? "bg-white text-blue-600 shadow-sm dark:bg-boxdark dark:text-blue-400"
                      : "hover:text-gray-900 dark:hover:text-gray-100"
                  }`}
                >
                  Feedbacks de Treinos
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
              {activeInfoTab === "appointments" ? (
                <>
                  {loadingAppointments ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                    </div>
                  ) : appointments.length > 0 ? (
                    <div className="flex flex-col gap-3 sm:gap-4">
                      {appointments.map((apt) => (
                        <AppointmentItem
                          key={apt.id}
                          appointment={apt}
                          allAppointments={appointments}
                          showActions={false}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-12 dark:border-gray-700">
                      <FiClock className="mb-3 text-gray-400" size={32} />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Nenhum agendamento encontrado
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {loadingFeedbacks ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
                    </div>
                  ) : feedbacks.length > 0 ? (
                    <div className="flex flex-col gap-3 sm:gap-4">
                      {feedbacks.map((feedback) => (
                        <div
                          key={feedback.id}
                          className="rounded-xl border border-stroke bg-gray-50 p-3 shadow-sm transition-all dark:border-strokedark dark:bg-gray-900/40 sm:p-4 md:hover:shadow-md"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                Plano de Treino
                              </p>
                              <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                                {feedback.workout_plan_name ||
                                  `Plano #${feedback.workout_plan}`}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                              <span>{feedback.rating}</span>
                              <span className="text-[10px] opacity-80">
                                / 5
                              </span>
                            </div>
                          </div>
                          {feedback.comment && (
                            <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                              {feedback.comment}
                            </p>
                          )}
                          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                            {feedback.created_at
                              ? new Date(feedback.created_at).toLocaleString(
                                  "pt-BR",
                                )
                              : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 py-12 dark:border-gray-700">
                      <FiAlertCircle className="mb-3 text-gray-400" size={32} />
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Nenhum feedback de treino encontrado
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                        Assim que o aluno enviar feedbacks dos treinos eles
                        aparecerão aqui.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Info Box - Mobile First */}
        <div className="mt-2 mb-8 rounded-xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-2.5 dark:border-blue-800 dark:from-blue-900/20 dark:to-cyan-900/20 sm:mb-10 sm:rounded-2xl sm:p-3">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="flex-shrink-0 rounded-lg bg-blue-100 p-1.5 dark:bg-blue-900/40 sm:rounded-xl sm:p-2">
              <FiAlertCircle
                className="text-blue-600 dark:text-blue-400"
                size={18}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h5 className="mb-0.5 text-xs font-bold text-blue-900 dark:text-blue-300 sm:mb-1 sm:text-sm">
                Informações Importantes
              </h5>
              <ul className="space-y-0.5 text-[11px] leading-relaxed text-blue-800 dark:text-blue-400 sm:space-y-1 sm:text-xs">
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-blue-600">•</span>
                  <span>
                    Alterações no e-mail podem afetar o acesso do aluno ao
                    sistema
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-blue-600">•</span>
                  <span>
                    A data de validade controla o acesso aos planos de treino e
                    avaliações
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 text-blue-600">•</span>
                  <span>
                    Recomendamos notificar o aluno antes de alterar dados
                    importantes
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Botões de Ação - Footer Fixo - Aparece apenas se houver mudanças - Mobile First */}
      {hasChanges && (
        <div className="sticky bottom-0 mt-auto flex flex-col items-stretch justify-between gap-2 rounded-t-2xl border-t border-stroke bg-white/95 px-3 py-3 shadow-lg backdrop-blur-md dark:border-strokedark dark:bg-boxdark/95 sm:flex-row sm:items-center sm:gap-0 sm:rounded-2xl sm:px-6 sm:py-4">
          <p className="hidden text-sm text-gray-600 dark:text-gray-400 sm:block">
            Campos marcados com <span className="text-red-500">*</span> são
            obrigatórios
          </p>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={() => {
                setProfileForm(originalForm);
                setHasChanges(false);
              }}
              disabled={savingProfile}
              className="order-2 min-h-[48px] w-full rounded-xl border-2 border-stroke px-4 py-2.5 font-semibold text-gray-700 transition-all touch-manipulation disabled:opacity-50 dark:border-strokedark dark:text-white sm:order-1 sm:min-h-0 sm:w-auto sm:px-6 sm:py-3 md:hover:bg-white md:dark:hover:bg-boxdark"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={savingProfile}
              className="order-1 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2.5 font-semibold text-white shadow-sm transition-all touch-manipulation disabled:opacity-50 sm:order-2 sm:min-h-0 sm:w-auto sm:px-8 sm:py-3 md:hover:scale-105 md:hover:shadow-xl md:disabled:hover:scale-100"
            >
              {savingProfile ? (
                <>
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <FiSave size={20} />
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

export default StudentProfileTab;
